import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const USER_POOL_CLIENT_ID = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined;
// AppSync Events endpoint for real-time notifications (ADR-0037). Absent → the
// portal stays pure-polling; realtime.ts no-ops. Cognito IDs + this endpoint are
// not secrets (the endpoint is reachable only with a valid per-tenant idToken).
const REALTIME_ENDPOINT = import.meta.env.VITE_REALTIME_ENDPOINT as string | undefined;

/** Returns true if Cognito is configured in the runtime env. */
export const authReady = Boolean(USER_POOL_ID && USER_POOL_CLIENT_ID);

if (authReady) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: USER_POOL_ID!,
        userPoolClientId: USER_POOL_CLIENT_ID!,
        loginWith: { email: true },
      },
    },
    // Real-time push (ADR-0037). authMode 'lambda' — the per-tenant idToken is
    // passed per connect/subscribe in realtime.ts; the authorizer enforces
    // channel↔tenant isolation. Only wired when the endpoint is configured.
    ...(REALTIME_ENDPOINT
      ? {
          API: {
            Events: {
              endpoint: REALTIME_ENDPOINT,
              region: "us-east-1",
              defaultAuthMode: "lambda" as const,
            },
          },
        }
      : {}),
  });
}

export type AuthRole = "admin" | "client" | null;

/**
 * Sign-in errors are surfaced as a structured `code` + the human
 * `message` so the SPA can render mapped guidance + recovery links.
 *
 * The pool runs `PreventUserExistenceErrors=ENABLED` (verified
 * 2026-04-25 via `aws cognito-idp describe-user-pool-client` — see
 * docs/verification/login-multi-session/pool-config.txt). This
 * deliberately collapses NotAuthorizedException (wrong password)
 * and UserNotFoundException (no such user) into a single generic
 * response, to defeat user enumeration. The downstream effect we
 * have to handle: a stale password (e.g. password-manager autofill
 * after a rotation) looks IDENTICAL to "no such user" looks
 * IDENTICAL to "blocked because logged in elsewhere" — they are
 * all NotAuthorizedException with the same generic error.
 *
 * Cognito does NOT block concurrent sign-in (multi-session.spec.ts
 * verifies this end-to-end). So the right SPA UX on
 * NotAuthorizedException is: assume stale credentials, link to
 * "Forgot password?" prominently. The "logged in elsewhere"
 * mental model is incorrect; we don't have to support it.
 */
export type SignInErrorCode =
  | "not-authorized"          // wrong password / no such user / locked (cannot disambiguate)
  | "limit-exceeded"          // too many attempts in window
  | "password-reset-required" // forced rotation by admin
  | "user-not-confirmed"      // edge case (invite emails are pre-confirmed)
  | "already-signed-in"       // Bug 4 — Amplify throws when an active session exists
  | "network"                 // fetch failed — offline, CSP, DNS
  | "unknown";

export type SignInResult =
  | { kind: "ok"; role: AuthRole }
  | { kind: "new-password-required" }
  | { kind: "mfa-required" }
  | { kind: "error"; code: SignInErrorCode; message: string; recoveryHref?: string };

function mapAmplifyError(err: unknown): { code: SignInErrorCode; message: string; recoveryHref?: string } {
  // Amplify v6 throws errors with a `.name` matching the Cognito
  // exception class. Fall back to message-sniffing for older shapes.
  const name = (err as { name?: string } | null)?.name ?? "";
  const rawMsg = err instanceof Error ? err.message : "";
  if (name === "NotAuthorizedException" || /Incorrect username or password|password attempts exceeded|User is disabled/i.test(rawMsg)) {
    return {
      code: "not-authorized",
      message:
        "We couldn't sign you in with that email and password. " +
        "If you've recently rotated the password, your password manager may have a stale value cached — " +
        "use the Forgot password link below to reset it. " +
        "Concurrent sign-ins from multiple browsers are supported and don't cause this error.",
      recoveryHref: "/portal/forgot",
    };
  }
  if (name === "LimitExceededException" || /Attempt limit exceeded/i.test(rawMsg)) {
    return {
      code: "limit-exceeded",
      message: "Too many sign-in attempts. Please wait 15 minutes and try again, or use Forgot password to reset.",
      recoveryHref: "/portal/forgot",
    };
  }
  if (name === "PasswordResetRequiredException" || /Password reset required/i.test(rawMsg)) {
    return {
      code: "password-reset-required",
      message: "An admin has requested a password reset for your account. Use Forgot password to set a new one.",
      recoveryHref: "/portal/forgot",
    };
  }
  if (name === "UserNotConfirmedException") {
    return {
      code: "user-not-confirmed",
      message: "Your account hasn't been confirmed. Check the invite email for a temporary password, or contact NEXIRT.",
    };
  }
  if (name === "UserAlreadyAuthenticatedException" || /already.*signed.*in|already.*authenticated/i.test(rawMsg)) {
    return {
      code: "already-signed-in",
      message: "You're already signed in. Continue to the portal, or sign out to switch accounts.",
      recoveryHref: "/portal",
    };
  }
  if (name === "NetworkError" || name === "TypeError" || /Failed to fetch|NetworkError/i.test(rawMsg)) {
    return {
      code: "network",
      message: "Couldn't reach the sign-in service. Check your connection and try again.",
    };
  }
  return {
    code: "unknown",
    message: rawMsg || "Sign-in failed. Email contact@nexirt.com if this persists.",
  };
}

export async function signIn(username: string, password: string): Promise<SignInResult> {
  if (!authReady) return {
    kind: "error",
    code: "unknown",
    message: "Authentication is coming online shortly. Email contact@nexirt.com for urgent access.",
  };
  try {
    const res = await amplifySignIn({ username, password });
    const step = res.nextStep.signInStep;
    if (step === "DONE") {
      const role = await currentRole();
      return { kind: "ok", role };
    }
    if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") return { kind: "new-password-required" };
    if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" || step === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") return { kind: "mfa-required" };
    return { kind: "error", code: "unknown", message: `Unhandled step: ${step}` };
  } catch (err) {
    return { kind: "error", ...mapAmplifyError(err) };
  }
}

export async function completeNewPassword(newPassword: string): Promise<SignInResult> {
  try {
    const res = await confirmSignIn({ challengeResponse: newPassword });
    if (res.nextStep.signInStep === "DONE") {
      const role = await currentRole();
      return { kind: "ok", role };
    }
    return { kind: "error", code: "unknown", message: `Unhandled step: ${res.nextStep.signInStep}` };
  } catch (err) {
    return { kind: "error", ...mapAmplifyError(err) };
  }
}

export async function forgot(username: string) {
  return resetPassword({ username });
}

export async function confirmForgot(username: string, code: string, newPassword: string) {
  return confirmResetPassword({ username, confirmationCode: code, newPassword });
}

export async function signOut() {
  await amplifySignOut();
}

async function currentRole(): Promise<AuthRole> {
  try {
    const session = await fetchAuthSession();
    const groupsRaw = session.tokens?.idToken?.payload["cognito:groups"];
    const groups = Array.isArray(groupsRaw) ? groupsRaw.map(String) : [];
    if (groups.includes("admin")) return "admin";
    if (groups.includes("client")) return "client";
    return null;
  } catch {
    return null;
  }
}

export async function getMe() {
  try {
    const user = await getCurrentUser();
    const role = await currentRole();
    return { user, role };
  } catch {
    return null;
  }
}
